import { DOCUMENT } from '@angular/common';
import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { AppConfigService } from '@app/services/app-config.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { BaseURL } from "@app/shared/common/serviceURL";

@Injectable({ providedIn: 'root' })
export class ActivePlanService implements OnDestroy {
  private readonly activePlanStorageKey = 'currentPlanID';
  private readonly window = inject(DOCUMENT).defaultView;
  private activePlanSubject = new BehaviorSubject<any>(this.readStoredActivePlan());
  activePlan$ = this.activePlanSubject.asObservable();
  private publishedPlanStateSubject = new BehaviorSubject<boolean>(false);
  publishedPlanState$ = this.publishedPlanStateSubject.asObservable();
  private baseURL = BaseURL;
  private readonly httpClient = inject(HttpClient);
  private apiUrl = AppConfigService.settings.apiUrl;
  private readonly publishedPlanStateStorageKey = 'publishedPlanState';

  private readonly handleStorageChange = (event: StorageEvent): void => {
    if (event.key !== this.activePlanStorageKey) {
      return;
    }

    const planId = event.newValue;
    this.activePlanSubject.next(
      planId && planId !== '0' ? { id: planId } : null
    );
  };

  constructor() {
    this.window?.addEventListener('storage', this.handleStorageChange);
  }

  setActivePlan(plan: any) {
    const nextPlan = plan?.refresh && plan?.id == null
      ? { ...(this.activePlanSubject.value ?? this.readStoredActivePlan() ?? {}), ...plan }
      : plan;

    if (nextPlan?.readonlyReason !== undefined) {
      this.setPublishedPlanState(
        (nextPlan.readonlyReason as string)?.toLowerCase() === 'published'
      );
    }

    if (nextPlan?.id !== undefined && nextPlan?.id !== null) {
      this.window?.localStorage.setItem(this.activePlanStorageKey, String(nextPlan.id));
    }

    this.activePlanSubject.next(nextPlan);
  }

  clearActivePlan() {
    this.setPublishedPlanState(false);
    this.window?.localStorage.setItem(this.activePlanStorageKey, '0');
    this.activePlanSubject.next(null);
  }

  getActivePlan() {
    return this.activePlanSubject.value;
  }

  getRegion() {
    return (localStorage.getItem('selectedRegion') || '')
  }

  setPublishedPlanState(state: boolean) {
    this.publishedPlanStateSubject.next(state);
    localStorage.setItem(this.publishedPlanStateStorageKey, JSON.stringify(state));
  }

  markCurrentPlanPublished() {
    const currentPlanId = localStorage.getItem('currentPlanID') || this.activePlanSubject.value?.id;
    const activePlan = this.activePlanSubject.value || {};

    this.setActivePlan({
      ...activePlan,
      id: activePlan.id ?? currentPlanId,
      planId: activePlan.planId ?? currentPlanId,
      readonlyReason: 'Published'
    });
  }

  readStoredPublishedPlanState(): boolean | null {
    const storedState = localStorage.getItem(this.publishedPlanStateStorageKey);

    if (storedState === null) {
      return null;
    }

    try {
      return JSON.parse(storedState) as boolean;
    } catch {
      return null;
    }
  }

  isPlanPublised(): Observable<any> {
    const planId = localStorage.getItem('currentPlanID') || '';
    const region = this.getRegion();
    const fullUrl = `${this.apiUrl}/publish-plan/${planId}/${region}`;

    return this.httpClient.get(fullUrl);
  }

  ngOnDestroy(): void {
    this.window?.removeEventListener('storage', this.handleStorageChange);
  }

  private readStoredActivePlan(): any {
    const planId = this.window?.localStorage.getItem(this.activePlanStorageKey);
    return planId && planId !== '0' ? { id: planId } : null;
  }

}
